import React, { useState } from 'react';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider,
} from '@mui/material';
import {
  Home as HomeIcon,
  Assignment as OrdersIcon,
  Train as RailcarIcon,
  People as PeopleIcon,
  Place as LocationIcon,
  LocalShipping as ShipperIcon,
  Category as MaterialIcon,
  Inventory2 as GroundInventoryIcon,
  PersonPin as ReceiverIcon,
  HelpOutline as HelpIcon,
  ExpandLess,
  ExpandMore,
  Login as LoginIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { getStoredUser } from '../auth';

const SideNav = ({ open, onClose }) => {
  const [ordersOpen, setOrdersOpen] = useState(false);
  const user = getStoredUser();
  const location = useLocation();
  const [railcarsOpen, setRailcarsOpen] = useState(location.pathname.startsWith('/railcars'));
  const orderStatusFilter = new URLSearchParams(location.search).get('status');
  const isCustomer = user?.role === 'customer';
  const isInternal = user?.role === 'internal' || user?.role === 'admin';
  const isAdmin = user?.role === 'admin';
  const customerGroundInventoryEnabled = isCustomer && Boolean(user?.customer?.enableGroundInventory);

  const handleOrdersClick = (e) => {
    e.stopPropagation();
    setOrdersOpen((prev) => !prev);
  };

  const handleRailcarsClick = (e) => {
    e.stopPropagation();
    setRailcarsOpen((prev) => !prev);
  };

  const navButtonSx = (to, isActive = location.pathname === to) => ({
    mx: 1,
    my: 0.3,
    px: 1.25,
    borderRadius: 2,
    minHeight: 44,
    color: 'text.primary',
    '& .MuiListItemIcon-root': {
      minWidth: 34,
      color: 'text.secondary',
    },
    ...(isActive
      ? {
          backgroundColor: 'var(--ui-brand-soft)',
          color: 'var(--ui-brand)',
          '& .MuiListItemIcon-root': { color: 'var(--ui-brand)' },
          '&:hover': { backgroundColor: '#dfdad1' },
        }
      : {
          '&:hover': { backgroundColor: '#f3f1ed' },
        }),
  });

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 280,
          borderRight: '1px solid var(--ui-border)',
          background: 'linear-gradient(180deg, #ffffff 0%, #faf9f7 100%)',
        },
      }}
    >
      <Box sx={{ width: 280 }} role="presentation" onClick={onClose} onKeyDown={onClose}>
        <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <Typography variant="overline" sx={{ color: 'var(--ui-muted)', letterSpacing: 1.2 }}>
            Tennessee Rail Systems
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--ui-brand)', lineHeight: 1.1 }}>
            Transload Portal
          </Typography>
        </Box>
        <Divider sx={{ mb: 1 }} />
        <List>
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/" sx={navButtonSx('/')}>
              <ListItemIcon>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText primary="Home" />
            </ListItemButton>
          </ListItem>

          {!user && (
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/login" sx={navButtonSx('/login')}>
                <ListItemIcon>
                  <LoginIcon />
                </ListItemIcon>
                <ListItemText primary="Login" />
              </ListItemButton>
            </ListItem>
          )}

          {user && (
            <>
              {isCustomer ? (
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/orders" sx={navButtonSx('/orders')}>
                    <ListItemIcon>
                      <OrdersIcon />
                    </ListItemIcon>
                    <ListItemText primary="Orders" />
                  </ListItemButton>
                </ListItem>
              ) : (
                <>
                  <ListItem disablePadding>
                    <ListItemButton onClick={handleOrdersClick} sx={navButtonSx('/orders')}>
                      <ListItemIcon>
                        <OrdersIcon />
                      </ListItemIcon>
                      <ListItemText primary="Orders" />
                      {ordersOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={ordersOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <ListItem disablePadding>
                        <ListItemButton
                          sx={{ ...navButtonSx('/orders', location.pathname === '/orders' && !orderStatusFilter), pl: 4.5 }}
                          component={Link}
                          to="/orders"
                        >
                          <ListItemText primary="All Orders" />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton
                          sx={{ ...navButtonSx('/orders', location.pathname === '/orders' && orderStatusFilter === 'open'), pl: 4.5 }}
                          component={Link}
                          to="/orders?status=open"
                        >
                          <ListItemText primary="Open Orders" />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton
                          sx={{ ...navButtonSx('/orders', location.pathname === '/orders' && orderStatusFilter === 'shipped'), pl: 4.5 }}
                          component={Link}
                          to="/orders?status=shipped"
                        >
                          <ListItemText primary="Shipped Orders" />
                        </ListItemButton>
                      </ListItem>
                    </List>
                  </Collapse>
                </>
              )}

              <ListItem disablePadding>
                <ListItemButton component={Link} to="/bols" sx={navButtonSx('/bols')}>
                  <ListItemIcon>
                    <OrdersIcon />
                  </ListItemIcon>
                  <ListItemText primary={isInternal ? 'BOL Queue' : 'BOLs'} />
                </ListItemButton>
              </ListItem>

              {isInternal ? (
                <>
                  <ListItem disablePadding>
                    <ListItemButton onClick={handleRailcarsClick} sx={navButtonSx('/railcars', location.pathname.startsWith('/railcars'))}>
                      <ListItemIcon>
                        <RailcarIcon />
                      </ListItemIcon>
                      <ListItemText primary="Railcars" />
                      {railcarsOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={railcarsOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <ListItem disablePadding>
                        <ListItemButton sx={{ ...navButtonSx('/railcars', location.pathname === '/railcars'), pl: 4.5 }} component={Link} to="/railcars">
                          <ListItemText primary="All Railcars" />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton sx={{ ...navButtonSx('/railcars/unloads'), pl: 4.5 }} component={Link} to="/railcars/unloads">
                          <ListItemText primary="Railcar Unloads" />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton sx={{ ...navButtonSx('/railcars/upload'), pl: 4.5 }} component={Link} to="/railcars/upload">
                          <ListItemText primary="Railcar Upload" />
                        </ListItemButton>
                      </ListItem>
                    </List>
                  </Collapse>
                </>
              ) : (
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/railcars" sx={navButtonSx('/railcars')}>
                    <ListItemIcon>
                      <RailcarIcon />
                    </ListItemIcon>
                    <ListItemText primary="Railcars" />
                  </ListItemButton>
                </ListItem>
              )}

              {(isInternal || customerGroundInventoryEnabled) && (
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/ground-inventory" sx={navButtonSx('/ground-inventory')}>
                    <ListItemIcon>
                      <GroundInventoryIcon />
                    </ListItemIcon>
                    <ListItemText primary="Ground Inventory" />
                  </ListItemButton>
                </ListItem>
              )}

              {isCustomer && (
                <>
                  <Typography variant="caption" sx={{ px: 2, pt: 1, pb: 0.5, color: 'var(--ui-muted)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    Customer Setup
                  </Typography>
                  <ListItem disablePadding>
                    <ListItemButton component={Link} to="/projects" sx={navButtonSx('/projects')}>
                      <ListItemIcon>
                        <LocationIcon />
                      </ListItemIcon>
                      <ListItemText primary="Locations" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton component={Link} to="/shippers" sx={navButtonSx('/shippers')}>
                      <ListItemIcon>
                        <ShipperIcon />
                      </ListItemIcon>
                      <ListItemText primary="Shippers" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton component={Link} to="/materials" sx={navButtonSx('/materials')}>
                      <ListItemIcon>
                        <MaterialIcon />
                      </ListItemIcon>
                      <ListItemText primary="Materials" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton component={Link} to="/receivers" sx={navButtonSx('/receivers')}>
                      <ListItemIcon>
                        <ReceiverIcon />
                      </ListItemIcon>
                      <ListItemText primary="Receivers" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton component={Link} to="/help" sx={navButtonSx('/help')}>
                      <ListItemIcon>
                        <HelpIcon />
                      </ListItemIcon>
                      <ListItemText primary="Help" />
                    </ListItemButton>
                  </ListItem>
                </>
              )}

              {isInternal && (
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/customers" sx={navButtonSx('/customers')}>
                    <ListItemIcon>
                      <PeopleIcon />
                    </ListItemIcon>
                    <ListItemText primary="Customers" />
                  </ListItemButton>
                </ListItem>
              )}

              {isAdmin && (
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/users" sx={navButtonSx('/users')}>
                    <ListItemIcon>
                      <PeopleIcon />
                    </ListItemIcon>
                    <ListItemText primary="Users" />
                  </ListItemButton>
                </ListItem>
              )}
            </>
          )}
        </List>
      </Box>
    </Drawer>
  );
};

export default SideNav;


